import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Footer from "@/components/Footer";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import { SITE } from "@/constants/site";

const Bloc = ({ titre, id, children }) => (
    <section id={id} className="scroll-mt-28" data-testid={`bloc-${id}`}>
        <h2 className="text-2xl text-terrat md:text-3xl">{titre}</h2>
        <hr className="rule-gold mt-3 w-16" />
        <div className="mt-5 space-y-4 text-ink">{children}</div>
    </section>
);

const Ligne = ({ label, children }) => (
    <p>
        <span className="font-semibold">{label} : </span>
        {children}
    </p>
);

export default function MentionsLegales() {
    const { hash } = useLocation();

    useSeo({
        title: "Mentions légales et confidentialité — Aurassium Clinic",
        description:
            "Mentions légales du site Aurassium Clinic : éditeur, profession réglementée, hébergement, propriété intellectuelle, données personnelles (RGPD) et cookies.",
        path: "/mentions-legales",
    });

    useEffect(() => {
        let cible = null;
        try {
            cible = hash ? document.querySelector(hash) : null;
        } catch {
            cible = null;
        }
        const y = cible ? cible.getBoundingClientRect().top + window.scrollY - 100 : 0;
        if (window.__lenis) {
            window.__lenis.scrollTo(y, { immediate: true });
        } else {
            window.scrollTo(0, y);
        }
    }, [hash]);

    return (
        <>
            <header
                className="fixed inset-x-0 top-0 z-40"
                style={{
                    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(251,247,242,0.88) 100%)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    borderBottom: "1px solid var(--c-line)",
                }}
            >
                <div className="mx-auto flex max-w-[1080px] items-center justify-between gap-4 px-5 py-3.5">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-ink transition-colors duration-200 hover:text-terrat"
                        data-testid="back-home-link"
                    >
                        <ArrowLeft size={16} aria-hidden="true" />
                        Retour à l'accueil
                    </Link>
                    <a
                        href={SITE.doctolibUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                        data-testid="legal-rdv-button"
                    >
                        Prendre rendez-vous sur
                        <DoctolibLogo height={13} color="#FFFFFF" />
                    </a>
                </div>
            </header>

            <main className="grad-hero pt-24 md:pt-28" data-testid="legal-page">
                <div className="mx-auto max-w-[760px] px-5 pb-16 md:pb-24">
                    <p className="eyebrow">Informations légales</p>
                    <h1 className="title-grad mt-3 text-4xl sm:text-5xl">
                        Mentions légales et confidentialité
                    </h1>
                    <p className="mt-5 text-lg text-ink2">
                        Dernière mise à jour : octobre 2026.
                    </p>

                    <nav className="mt-8 flex flex-wrap gap-2.5" aria-label="Sommaire">
                        {[
                            ["#editeur", "Éditeur"],
                            ["#profession", "Profession"],
                            ["#hebergement", "Hébergement"],
                            ["#propriete", "Propriété intellectuelle"],
                            ["#confidentialite", "Données personnelles"],
                            ["#cookies", "Cookies"],
                        ].map(([href, label]) => (
                            <a
                                key={href}
                                href={href}
                                className="rounded-full bg-white/70 px-4 py-1.5 text-sm text-ink transition-colors duration-200 hover:text-terrat"
                                style={{ border: "1px solid var(--c-line)" }}
                            >
                                {label}
                            </a>
                        ))}
                    </nav>

                    <div className="mt-12 space-y-12">
                        <Bloc titre="Éditeur du site" id="editeur">
                            <Ligne label="Nom commercial">{SITE.name}</Ligne>
                            <Ligne label="Responsable de la publication">
                                {SITE.doctor}, docteur en médecine
                            </Ligne>
                            <Ligne label="Adresse du cabinet">{SITE.address}</Ligne>
                            <Ligne label="N° RPPS">{SITE.rpps}</Ligne>
                            <Ligne label="N° SIRET">{SITE.siret}</Ligne>
                        </Bloc>

                        <Bloc titre="Profession réglementée" id="profession">
                            <p>
                                L'exercice de la médecine est une profession
                                réglementée. Le {SITE.doctor} est inscrit au tableau
                                du Conseil départemental de l'Ordre des médecins des
                                Hauts-de-Seine (92).
                            </p>
                            <p>
                                L'activité est soumise au code de la santé publique
                                et au code de déontologie médicale (articles
                                R.4127-1 et suivants du code de la santé publique),
                                consultable sur{" "}
                                <a
                                    href="https://www.conseil-national.medecin.fr"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-terrat"
                                >
                                    conseil-national.medecin.fr
                                </a>
                                .
                            </p>
                            <p>
                                Les informations publiées sur ce site ont une visée
                                d'information du public. Elles ne remplacent pas une
                                consultation médicale et ne constituent ni un
                                diagnostic, ni une promesse de résultat.
                            </p>
                        </Bloc>

                        <Bloc titre="Hébergement du site" id="hebergement">
                            <p className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold">
                                    Nom, adresse et téléphone de l'hébergeur :
                                </span>
                                <span className="badge-gold">À compléter</span>
                            </p>
                            <p className="text-sm text-ink2">
                                Ce site est un site vitrine : il n'héberge aucune
                                donnée de santé.
                            </p>
                        </Bloc>

                        <Bloc titre="Propriété intellectuelle" id="propriete">
                            <p>
                                L'ensemble des contenus de ce site (textes,
                                photographies, illustrations, identité visuelle)
                                est protégé par le droit d'auteur. Toute
                                reproduction, même partielle, est interdite sans
                                autorisation écrite préalable.
                            </p>
                            <p>
                                Les images d'illustration médicale présentées dans
                                la rubrique « Les soins » sont des images de
                                synthèse à visée pédagogique. Elles ne représentent
                                aucune patiente du cabinet.
                            </p>
                            <p>
                                Les photographies du cabinet et le portrait du
                                médecin sont la propriété d'{SITE.name}.
                            </p>
                        </Bloc>

                        <Bloc titre="Données personnelles et confidentialité" id="confidentialite">
                            <Ligne label="Responsable de traitement">
                                {SITE.doctor}, {SITE.address}
                            </Ligne>
                            <p>
                                Ce site est un site vitrine. Il ne comporte ni
                                formulaire, ni création de compte, ni paiement en
                                ligne : <strong>aucune donnée personnelle n'est
                                collectée par le site lui-même</strong>.
                            </p>
                            <p>
                                <span className="font-semibold">Prise de rendez-vous : </span>
                                elle se fait en ligne via Doctolib. Lorsque
                                vous utilisez le lien Doctolib, vos données sont
                                traitées par Doctolib, responsable de ce
                                traitement, selon sa propre politique de
                                confidentialité disponible sur{" "}
                                <a
                                    href="https://www.doctolib.fr/terms/agreement"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-terrat"
                                >
                                    doctolib.fr
                                </a>
                                .
                            </p>
                            <p>
                                <span className="font-semibold">Données de santé : </span>
                                les informations recueillies pendant la consultation
                                sont couvertes par le secret médical. Elles sont
                                conservées dans votre dossier médical, pour la durée
                                prévue par la réglementation, et ne sont jamais
                                cédées ni utilisées à des fins commerciales.
                            </p>
                            <p>
                                <span className="font-semibold">Vos droits : </span>
                                conformément au règlement (UE) 2016/679 (RGPD) et à
                                la loi « Informatique et Libertés », vous disposez
                                d'un droit d'accès, de rectification, d'effacement,
                                de limitation, d'opposition et de portabilité, ainsi
                                que du droit de définir des directives relatives au
                                sort de vos données après votre décès. Pour les
                                exercer, écrivez au cabinet à l'adresse ci-dessus. Une
                                pièce d'identité pourra être demandée et une réponse
                                vous sera apportée dans un délai d'un mois.
                            </p>
                            <p>
                                <span className="font-semibold">
                                    Base légale et destinataires :{" "}
                                </span>
                                les données recueillies en consultation le sont pour
                                l'exécution des soins et le respect des obligations
                                légales du médecin. Elles sont destinées au seul
                                médecin, tenu au secret professionnel, et ne font
                                l'objet d'aucun transfert en dehors de l'Union
                                européenne.
                            </p>
                            <p>
                                En cas de désaccord, vous pouvez introduire une
                                réclamation auprès de la CNIL,{" "}
                                <a
                                    href="https://www.cnil.fr"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-terrat"
                                >
                                    cnil.fr
                                </a>
                                .
                            </p>
                        </Bloc>

                        <Bloc titre="Cookies, mesure d'audience et services tiers" id="cookies">
                            <p>
                                <strong>
                                    Ce site ne dépose aucun cookie et n'utilise aucun
                                    outil de mesure d'audience ni de publicité.
                                </strong>{" "}
                                Aucun traceur n'est installé sur votre appareil :
                                aucun consentement n'est donc requis, conformément à
                                l'article 82 de la loi « Informatique et Libertés ».
                            </p>
                            <p>
                                <span className="font-semibold">
                                    Aucun appel à un serveur tiers :{" "}
                                </span>
                                les polices de caractères, les images et l'ensemble
                                des ressources du site sont hébergées sur le site
                                lui-même. Votre adresse IP n'est donc transmise à
                                aucun prestataire extérieur lors de la simple
                                consultation des pages — en particulier pas à Google
                                Fonts.
                            </p>
                            <p>
                                <span className="font-semibold">
                                    Si vous cliquez sur un lien externe :{" "}
                                </span>
                                les liens vers Doctolib, Google Maps et Instagram
                                ouvrent des sites tiers qui peuvent, à ce
                                moment-là seulement, déposer leurs propres cookies et
                                traiter vos données. Leurs politiques de
                                confidentialité s'appliquent alors ; vous restez libre
                                de ne pas les ouvrir.
                            </p>
                            <p>
                                <span className="font-semibold">Journaux serveur : </span>
                                comme tout site web, l'hébergeur conserve des
                                journaux de connexion techniques (adresse IP,
                                horodatage, pages demandées) à seule fin d'assurer la
                                sécurité et le bon fonctionnement du service. Ces
                                journaux ne sont ni exploités à des fins
                                statistiques, ni commerciales, et sont purgés
                                automatiquement par l'hébergeur.
                            </p>
                            <p className="text-sm text-ink2">
                                Si un outil de mesure d'audience venait à être ajouté
                                à l'avenir, un bandeau de consentement conforme aux
                                recommandations de la CNIL serait mis en place et
                                aucune mesure ne serait effectuée avant votre accord.
                            </p>
                        </Bloc>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
